import { z } from 'zod';
/** Companion button location PAGE/ROW/COL */
export const ButtonLocationSchema = z
    .string()
    .regex(/^\d+\/\d+\/\d+$/, 'Expected PAGE/ROW/COL format');
export const PositionConfigSchema = z
    .object({
    id: z.string().min(1),
    label: z.string().min(1),
    location: ButtonLocationSchema,
    /** Substring or regex tested against streamed SUB-STATE TEXT */
    liveMatch: z.string().min(1).optional(),
    /** Background COLOR from SUB-STATE when live (e.g. "#ff0000") */
    liveColor: z.string().min(1).optional(),
})
    .refine((pos) => Boolean(pos.liveMatch || pos.liveColor), {
    message: 'Each position needs liveMatch (text) or liveColor (background)',
});
export const BridgeConfigSchema = z.object({
    showId: z.string().min(1),
    positions: z.array(PositionConfigSchema).min(1),
});
/** Per-position live state sent to panels */
export const PositionStateSchema = z.object({
    id: z.string(),
    label: z.string(),
    live: z.boolean(),
    text: z.string().optional(),
    pending: z.boolean().optional(),
});
export const LiveSnapshotSchema = z.object({
    showId: z.string(),
    positions: z.array(PositionStateSchema),
    bridgeConnected: z.boolean(),
    updatedAt: z.string().datetime(),
});
/** WebSocket wire messages (panel <-> relay, bridge <-> relay) */
export const ClientRoleSchema = z.enum(['host', 'co-host', 'participant', 'unknown']);
export const ConnectPayloadSchema = z.discriminatedUnion('role', [
    z.object({
        role: z.literal('bridge'),
        showId: z.string(),
        token: z.string(),
    }),
    z.object({
        role: z.literal('panel'),
        showId: z.string(),
        zoomContextToken: z.string(),
        userId: z.string(),
        userRole: ClientRoleSchema,
    }),
]);
export const PressMessageSchema = z.object({
    action: z.literal('press'),
    positionId: z.string(),
});
export const GrantMessageSchema = z.object({
    action: z.literal('grant'),
    userId: z.string(),
});
export const RevokeMessageSchema = z.object({
    action: z.literal('revoke'),
    userId: z.string(),
});
export const PingMessageSchema = z.object({
    action: z.literal('ping'),
    showId: z.string().min(1).optional(),
});
export const SyncMessageSchema = z.object({
    action: z.literal('sync'),
    showId: z.string().min(1).optional(),
});
export const ClientMessageSchema = z.discriminatedUnion('action', [
    PressMessageSchema,
    GrantMessageSchema,
    RevokeMessageSchema,
    PingMessageSchema,
    SyncMessageSchema,
]);
/** Server -> client messages */
export const StateUpdateMessageSchema = z.object({
    type: z.literal('state'),
    snapshot: LiveSnapshotSchema,
});
export const BridgePresenceMessageSchema = z.object({
    type: z.literal('bridgePresence'),
    connected: z.boolean(),
});
export const PermissionsMessageSchema = z.object({
    type: z.literal('permissions'),
    canControl: z.boolean(),
    grantedUserIds: z.array(z.string()).optional(),
});
export const ErrorMessageSchema = z.object({
    type: z.literal('error'),
    message: z.string(),
});
export const PongMessageSchema = z.object({
    type: z.literal('pong'),
});
export const ServerMessageSchema = z.discriminatedUnion('type', [
    StateUpdateMessageSchema,
    BridgePresenceMessageSchema,
    PermissionsMessageSchema,
    ErrorMessageSchema,
    PongMessageSchema,
]);
/** Bridge -> relay internal messages */
export const BridgeStateUpdateSchema = z.object({
    action: z.literal('stateUpdate'),
    snapshot: LiveSnapshotSchema,
});
export const BridgePressAckSchema = z.object({
    action: z.literal('pressAck'),
    positionId: z.string(),
    success: z.boolean(),
});
export const BridgeMessageSchema = z.discriminatedUnion('action', [
    BridgeStateUpdateSchema,
    BridgePressAckSchema,
]);
/** Relay -> bridge messages */
export const RelayPressCommandSchema = z.object({
    action: z.literal('press'),
    positionId: z.string(),
    requestId: z.string().optional(),
});
export const RelayMessageSchema = z.discriminatedUnion('action', [
    RelayPressCommandSchema,
    PingMessageSchema,
    SyncMessageSchema,
]);
/** DynamoDB item helpers */
export const DdbKeys = {
    showPk: (showId) => `SHOW#${showId}`,
    stateSk: () => 'STATE',
    bridgeSk: () => 'BRIDGE',
    connSk: (connectionId) => `CONN#${connectionId}`,
    grantSk: (meetingId, userId) => `GRANT#${meetingId}#${userId}`,
    grantPrefix: (meetingId) => `GRANT#${meetingId}#`,
};
export function isHostOrCoHost(role) {
    return role === 'host' || role === 'co-host';
}
/** Default meeting id for local dev tokens when Zoom app context is unavailable. */
export const DEV_MEETING_ID = 'dev-meeting';
//# sourceMappingURL=schemas.js.map